import { useEffect, useState } from 'react';
import { useGlobal } from 'reactn';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import numeral from 'numeral';
import randomcolor from 'randomcolor';
import Trianglify from 'react-trianglify';
import { Drawer, Tag, Menu, Dropdown, Descriptions, Tooltip, Statistic } from 'antd';
import gql from 'graphql-tag';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import Card from '../Card';
import Button from '../Button';
import UpdateProjectForm from '../UpdateProjectForm/UpdateProjectForm';
import { GET_MY_PROJECTS, GET_ALL_PROJECTS, GET_PROJECT_BY_ID } from '../../../project-manager/queries';
import { showError, showSuccess } from '../../notifications';
import moment from 'moment';
import {
  GET_RESOURCES_BY_PROJECT_ID,
  GET_DELIVERABLES_BY_PROJECT_ID,
} from '../../../project-manager/queries';
import NewProjectForm from '../../../admin/components/NewProjectForm';
import Modal from '../Modal';

const Header = styled.div`
  height: 100px;
  background: ${props => props.background};
  position: relative;
  overflow: hidden;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
`;
const ClientName = styled.h2`
  position: absolute;
  bottom: -0.5rem;
  left: 0.5rem;
  margin: 0;
  text-transform: uppercase;
  font-weight: 600;
  background: white;
  padding: 1rem;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
`;
const ProjectDescription = styled.p`
  color: initial;
  max-height: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
`;

const ARCHIVE_PROJECT = gql`
  mutation archiveProject($projectId: ID!, $archived: Boolean!) {
    archiveProject(projectId: $projectId, archived: $archived)
  }
`;

const DELETE_PROJECT = gql`
  mutation deleteProject($projectId: ID!) {
    deleteProject(projectId: $projectId)
  }
`;
const REQUEST_PROJECT_SYNC = gql`
  mutation requestProjectSync($projectId: ID!) {
    requestProjectSync(projectId: $projectId) {
      jobId
      status
    }
  }
`;

const PROJECT_SYNC_STATUS = gql`
  query projectSyncStatus($projectId: ID!) {
    projectSyncStatus(projectId: $projectId) {
      jobId
      status
      completedAt
      lastError
    }
  }
`;

const UPDATE_BUDGET_USED = gql`
  mutation updateBudgetUsed($projectId: ID!) {
    updateBudgetUsed(projectId: $projectId)
  }
`;

const ProjectCard = ({
  project,
  truncate = false,
  liftOnHover = false,
  refetchQueries,
  showActions = false,
  children,
  history,
}) => {
  const client = useApolloClient();
  const [config] = useGlobal('config');
  const netSuiteEnabled = config['netsuite-enabled'] === 'true';
  const netSuiteUrl = config['netsuite-url'];
  const hoursBudgetingEnabled = config['hours-budgeting-deliverable'] === 'true';
  const costLabel = config['project-cost-label'];
  const projectDetailsFieldsString = config['project-details-fields'];
  let projectDetailsFields = [];
  if (projectDetailsFieldsString) {
    projectDetailsFields = JSON.parse(projectDetailsFieldsString);
  }
  const [lockEditing] = useGlobal('lockEditing');
  const [showModal, setShowModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [clonedProjectData, setClonedProjectData] = useState(null);
  const { data: resourcesData } = useQuery(GET_RESOURCES_BY_PROJECT_ID, {
    variables: { projectId: project._id },
  });
  const { data: deliverablesData } = useQuery(GET_DELIVERABLES_BY_PROJECT_ID, {
    variables: { projectId: project._id },
  });

  const budgetUsedPct = project.budgetUsed;

  const resources = resourcesData?.projectManagement?.project?.resources || [];
  const deliverables = deliverablesData?.projectManagement?.project?.deliverables || [];
  const totalAllocatedBudgetedHours = resources.reduce(
    (acc, resource) => acc + resource.budgetedHours,
    0,
  );
  const totalDeliverableBudgetedHours = deliverables.reduce(
    (acc, deliverable) => acc + deliverable.budgetedHours,
    0,
  );

  const [archiveProject] = useMutation(ARCHIVE_PROJECT, { refetchQueries });
  const [deleteProject] = useMutation(DELETE_PROJECT, {
    refetchQueries: [
      {
        query: GET_MY_PROJECTS,
      },
      {
        query: GET_ALL_PROJECTS,
      },
    ],
    awaitRefetchQueries: true,
  });
  const [activeSyncJobId, setActiveSyncJobId] = useState(null);
  const [requestProjectSync] = useMutation(REQUEST_PROJECT_SYNC, {
    variables: { projectId: project._id },
  });
  const { data: syncStatusData } = useQuery(PROJECT_SYNC_STATUS, {
    variables: { projectId: project._id },
    fetchPolicy: 'network-only',
    skip: !(showActions && lockEditing),
    pollInterval: activeSyncJobId ? 3000 : 0,
  });
  const syncJob = syncStatusData?.projectSyncStatus;

  useEffect(() => {
    if (!syncJob) return;

    if (!activeSyncJobId) {
      // A sync started elsewhere (another user/tab) is still running - reflect it.
      if (syncJob.status === 'QUEUED' || syncJob.status === 'RUNNING') {
        setActiveSyncJobId(syncJob.jobId);
      }
      return;
    }

    if (syncJob.jobId !== activeSyncJobId) {
      // A different job now exists for this project (ours finished and someone
      // else - or another tab - started a new one before our next poll).
      // Adopt it if it is still active, otherwise stop tracking so the button unsticks.
      if (syncJob.status === 'QUEUED' || syncJob.status === 'RUNNING') {
        setActiveSyncJobId(syncJob.jobId);
      } else {
        setActiveSyncJobId(null);
      }
      return;
    }

    if (syncJob.status === 'COMPLETED') {
      setActiveSyncJobId(null);
      showSuccess({ message: 'Project data refreshed from NetSuite' });
      client.reFetchObservableQueries();
    } else if (syncJob.status === 'FAILED') {
      setActiveSyncJobId(null);
      showError({
        message: `NetSuite refresh failed: ${syncJob.lastError || 'unknown error'}`,
      });
    }
  }, [syncJob, activeSyncJobId, client]);

  const [updateBudgetUsed] = useMutation(UPDATE_BUDGET_USED, { refetchQueries });
  const sectors = project.sectors || [];
  // Detail queries fetch projectClient; list queries only fetch client.
  const projectClient = project.projectClient || project.client;
  const endClient = project.endClient;

  const renderProjectDetailsFields = () => {
    return projectDetailsFields.map(field => {
      switch (field) {
        case 'projectCode':
          return (
            <Descriptions.Item key={field}>
              <Statistic 
                title="Project Code" 
                value={project.projectCode ? String(project.projectCode) : 'Not set'} 
                formatter={(value) => value}
              />
            </Descriptions.Item>
          );
        case 'projectOwner':
          return (
            <Descriptions.Item key={field}>
              <Statistic title="Project Owner" value={project.owner || 'Not set'} />
            </Descriptions.Item>
          );
        case 'startDate':
          return (
            <Descriptions.Item key={field}>
              <Statistic
                title="Start Date"
                value={
                  project.startDate ? moment(project.startDate).format('MM/DD/YYYY') : 'Not set'
                }
              />
            </Descriptions.Item>
          );
        case 'accountingCode':
          return (
            <Descriptions.Item key={field}>
              <Statistic
                title="Accounting Code"
                suffix={project.accountingCodeSuffix || ''}
                value={project.accountingCode || 'Not set'}
              />
            </Descriptions.Item>
          );
        case 'budgetUsed':
          return (
            <Descriptions.Item key={field}>
              <Statistic title="Budget Used" value={numeral(budgetUsedPct).format('%0.0')} />
            </Descriptions.Item>
          );

        case 'projectBudget':
          return (
            <Descriptions.Item key={field}>
              <Statistic
                title="Project Budget"
                value={numeral(project.budget).format('$0,0.[0]')}
              />
            </Descriptions.Item>
          );

        case 'costAndBudget':
          return (
            <Descriptions.Item key={field}>
              <Statistic
                title={`${costLabel || 'Billable'} / Budget`}
                value={numeral(project.totalCost).format('$0,0.[0]')}
                suffix={'/ ' + numeral(project.budget).format('$0,0.[0]')}
              />
            </Descriptions.Item>
          );

        case 'hoursUsed':
          if (hoursBudgetingEnabled) {
            return (
              <Descriptions.Item key={field}>
                <Statistic
                  title="Hours Used"
                  value={numeral(project.hoursUsedPct).format('%0.0')}
                />
              </Descriptions.Item>
            );
          }
          return null;
        case 'approvedAndBudgetedHours':
          if (hoursBudgetingEnabled) {
            return (
              <Descriptions.Item key={field}>
                <Statistic
                  title="Aprv. / Budgeted Hours"
                  value={numeral(project.totalApprovedHours).format('0,0.[0]')}
                  suffix={` / ${numeral(totalDeliverableBudgetedHours).format('0,0.[0]')}`}
                />
              </Descriptions.Item>
            );
          }
          return null;
        case 'allocatedAndBudgetedHours':
          if (hoursBudgetingEnabled) {
            return (
              <Descriptions.Item key={field}>
                <Statistic
                  title="Allocated / Budgeted Hours"
                  value={numeral(totalAllocatedBudgetedHours).format('0,0.[0]')}
                  suffix={` / ${numeral(totalDeliverableBudgetedHours).format('0,0.[0]')}`}
                />
              </Descriptions.Item>
            );
          }
          return null;
        case null:
          return <div key={field} />;
        default:
          return null;
      }
    });
  };
  return (
    <Card
      borderWrapper
      withShadow
      liftOnHover={liftOnHover}
      padded
      headerComponent={
        <Header background={randomcolor({ seed: project._id })}>
          <Trianglify
            cell_size={300}
            output="svg"
            seed={project._id}
            width={2500}
            height={500}
            x_colors={[
              randomcolor({ seed: project._id, luminosity: 'light' }),
              randomcolor({ seed: project._id, luminosity: 'dark' }),
            ]}
          />
          <ClientName>{projectClient?.shortName}</ClientName>
          <div className="absolute top-0 right-0 my-2">
            {project.archived && <Tag>Archived</Tag>}
            {project.status && <Tag>{project.status}</Tag>}
          </div>
        </Header>
      }
    >
      <>
        <div>
          {showActions && (
            <div className="float-right flex gap-2">
              {lockEditing && (
                <Button
                  disabled={!!activeSyncJobId}
                  loading={!!activeSyncJobId}
                  onClick={async () => {
                    try {
                      const res = await requestProjectSync();
                      const job = res?.data?.requestProjectSync;
                      if (job?.jobId) setActiveSyncJobId(job.jobId);
                    } catch (err) {
                      showError({ message: 'Could not start the NetSuite refresh' });
                    }
                  }}
                >
                  {activeSyncJobId ? 'Refreshing from NetSuite...' : 'Refresh Data from NetSuite'}
                </Button>
              )}
              <Button onClick={() => setShowModal(true)}>Edit Project</Button>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="archive"
                      onClick={async () => {
                        archiveProject({
                          variables: { projectId: project._id, archived: !project?.archived },
                        });
                      }}
                    >
                      {project.archived ? 'Unarchive' : 'Archive'}
                    </Menu.Item>
                    <Menu.Item
                      disabled={!project.archived || lockEditing}
                      key="delete"
                      onClick={async () => {
                        const res = await deleteProject({
                          variables: { projectId: project._id },
                        });
                        if (res.errors) {
                          const message = res.errors[0].message;
                          showError({ message });
                        } else {
                          history.replace(`/project-management/projects`);
                        }
                      }}
                    >
                      <Tooltip
                        placement="bottomLeft"
                        title={!project.archived && 'Only archived projects can be deleted'}
                      >
                        Delete
                      </Tooltip>
                    </Menu.Item>
                    <Menu.Item
                      disabled={project.archived}
                      key="recalculate"
                      onClick={async () => {
                        try {
                          await updateBudgetUsed({ variables: { projectId: project._id } });
                          await Promise.all([
                            client.query({
                              query: GET_PROJECT_BY_ID,
                              variables: { projectId: project._id },
                              fetchPolicy: 'network-only',
                            }),
                            client.query({
                              query: GET_DELIVERABLES_BY_PROJECT_ID,
                              variables: { projectId: project._id },
                              fetchPolicy: 'network-only',
                            }),
                            client.query({
                              query: GET_RESOURCES_BY_PROJECT_ID,
                              variables: { projectId: project._id },
                              fetchPolicy: 'network-only',
                            }),
                          ]);
                          showSuccess({ message: 'Budget used recalculated' });
                        } catch (e) {
                          showError({ message: 'Error recalculating budget used' });
                        }
                      }}
                    >
                      <Tooltip placement="bottomLeft" title="Recalculate budget used on project.">
                        Recalculate Budget Used
                      </Tooltip>
                    </Menu.Item>
                    <Menu.Item
                      disabled={project.archived}
                      key="emailTeam"
                      onClick={async () => {
                        const email = `mailto:${resources.map(member => member.email).join(';')}`;
                        const mailWindow = window.open(email);
                        setTimeout(() => {
                          if (mailWindow) {
                            mailWindow.close();
                          }
                        }, 200);
                      }}
                    >
                      Email Team
                    </Menu.Item>
                    {!netSuiteEnabled && (
                      <Menu.Item
                        key="clone"
                        onClick={() => {
                          setClonedProjectData({
                            ...project,
                            name: project.name ? project.name + ' (Copy)' : '',
                            _id: undefined,
                            archived: false,
                            status: undefined,
                            integration: undefined,
                            syncedAt: undefined,
                          });
                          setShowCloneModal(true);
                        }}
                      >
                        Clone Project
                      </Menu.Item>
                    )}
                  </Menu>
                }
              >
                <Button shape="circle" icon="ellipsis" />
              </Dropdown>
            </div>
          )}
          <h2>
            {project.name?.length > 50 && truncate
              ? project.name.substring(0, 50 - 3) + '...'
              : project.name}
          </h2>
          {netSuiteEnabled && endClient && (
            <div className="mb-2" data-testid="end-client">
              End client: <strong>{endClient.name}</strong>{' '}
              <Tag color={project.showEndClient ? 'green' : undefined}>
                {project.showEndClient ? 'Shown to employees' : 'Not shown to employees'}
              </Tag>
            </div>
          )}
          {sectors.length > 0 && (
            <div className="mb-2">
              {sectors.map(sector => (
                <Tag key={sector}>{sector}</Tag>
              ))}
            </div>
          )}
          {children ? (
            children
          ) : (
            <ProjectDescription>
              {project.description?.length > 150 && truncate
                ? project.description.substring(0, 150 - 3) + '...'
                : project.description}
            </ProjectDescription>
          )}
        </div>
        <Descriptions>{renderProjectDetailsFields()}</Descriptions>
        {netSuiteEnabled && project?.integration?.sourceId && (
          <div className="w-full">
            <a
              href={`${netSuiteUrl}/app/accounting/project/project.nl?id=${project.integration.sourceId}&e=T`}
              target="_blank"
              rel="noreferrer"
            >
              <div className="flex justify-end">This project is managed by NetSuite</div>
            </a>
            <div className="flex justify-end">
              Last synced {moment(project?.syncedAt).format('LLL')}
            </div>
          </div>
        )}
        <Drawer
          title="Update Project"
          width="50%"
          visible={showModal}
          destroyOnClose
          closable
          onClose={() => setShowModal(false)}
          footer={null}
        >
          <UpdateProjectForm
            onComplete={() => setShowModal(false)}
            project={project}
            refetchQueries={refetchQueries}
          />
        </Drawer>
        <Modal
          title="Clone Project"
          visible={showCloneModal}
          footer={null}
          onCancel={() => setShowCloneModal(false)}
        >
          {clonedProjectData && (
            <NewProjectForm
              onComplete={projectId => {
                setShowCloneModal(false);
                if (projectId) {
                  history.push(`/project-management/projects/${projectId}`);
                }
              }}
              initialValues={{
                name: clonedProjectData.name || '',
                projectCode: clonedProjectData.projectCode || '',
                description: clonedProjectData.description || '',
                templateId: clonedProjectData._id,
                existingClientId: projectClient?._id || '',
                clientName: projectClient?.name || '',
                clientShortName: projectClient?.shortName || '',
                budget: clonedProjectData.budget || undefined,
              }}
              cloneFromProjectId={project._id}
            />
          )}
        </Modal>
      </>
    </Card>
  );
};

export default withRouter(ProjectCard);
